// One logged agent action (agent v2 `actions`, migration 278) with the button
// it allows: Undo for a done album change, Approve / Reject for a money
// decision waiting for the owner. Shared by the Approvals tab and the
// per-chat list in the conversation view.
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { whatsappApi, type AgentActionItem } from '@/lib/api';
import { useT } from '@/lib/i18n';
import { actionTitle, detailLine, STATUS_LABEL, STATUS_TONE, whenLabel } from './agentActionsView';

/** Every list of actions shares this key prefix, so one decision refreshes them all. */
export const AGENT_ACTIONS_KEY = 'agent-v2-actions';

export function AgentActionRow({ item, onOpenChat }: { item: AgentActionItem; onOpenChat?: (chatId: string) => void }) {
  const { t } = useT();
  const qc = useQueryClient();
  const [msg, setMsg] = useState<string | null>(null);
  const run = useMutation({
    mutationFn: (kind: 'undo' | 'approve' | 'reject') => (kind === 'undo'
      ? whatsappApi.undoAgentAction(item.id)
      : kind === 'approve' ? whatsappApi.approveAgentAction(item.id) : whatsappApi.rejectAgentAction(item.id)),
    onSuccess: (r, kind) => {
      setMsg(kind === 'approve' && (r as { manual?: boolean }).manual
        ? t('Validé — aucun outil automatique pour cette action : à faire à la main.')
        : null);
      void qc.invalidateQueries({ queryKey: [AGENT_ACTIONS_KEY] });
    },
    onError: (e: Error) => setMsg(e.message),
  });
  const pending = item.status === 'pending_approval';
  const detail = detailLine(item);

  return (
    <div className="rounded-lg border border-gray-100 bg-white px-3.5 py-2.5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[13px] font-semibold text-gray-800">{t(actionTitle(item))}</span>
            <span className={`text-[10.5px] font-semibold rounded-full border px-2 py-0.5 ${STATUS_TONE[item.status]}`}>
              {t(STATUS_LABEL[item.status])}
            </span>
          </div>
          {detail && <p className="text-[11.5px] text-gray-600 mt-1 leading-snug break-words">{detail}</p>}
          <p className="text-[11px] text-gray-400 mt-0.5 tabular-nums">
            {whenLabel(item.created_at)}
            {onOpenChat && (
              <>
                {' · '}
                <button type="button" className="underline hover:text-gray-700 cursor-pointer" onClick={() => onOpenChat(item.chat_id)}>
                  {item.chat_id.replace(/@.*$/, '')}
                </button>
              </>
            )}
          </p>
        </div>
        <div className="flex gap-1.5 shrink-0">
          {pending && (
            <>
              <button type="button" disabled={run.isPending} onClick={() => run.mutate('approve')}
                className="h-8 px-3 rounded-md bg-brand-500 text-white text-[12px] font-semibold cursor-pointer disabled:opacity-50">
                {t('Valider')}
              </button>
              <button type="button" disabled={run.isPending} onClick={() => run.mutate('reject')}
                className="h-8 px-3 rounded-md border border-gray-200 text-[12px] text-gray-700 cursor-pointer disabled:opacity-50">
                {t('Refuser')}
              </button>
            </>
          )}
          {item.status === 'done' && (
            <button type="button" disabled={run.isPending || !item.can_undo}
              title={item.can_undo ? t('Remettre l’album comme avant cette action') : (item.undo_blocker ?? '')}
              onClick={() => { if (window.confirm(t('Annuler cette action et remettre l’album comme avant ?'))) run.mutate('undo'); }}
              className="h-8 px-3 rounded-md border border-gray-200 text-[12px] text-gray-700 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed">
              {t('Annuler')}
            </button>
          )}
        </div>
      </div>
      {msg && <p className="text-[11.5px] text-amber-700 mt-1.5" role="status">{msg}</p>}
    </div>
  );
}
